import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MenuOverlay from '../components/MenuOverlay';
import CartDrawer from '../components/shop/CartDrawer';
import ProductGrid from '../components/shop/ProductGrid';
import useCart from '../hooks/useCart';
import { CATEGORIES, PRODUCTS } from '../data/products';

const Collection = () => {
  const { categoryId } = useParams();
  const category = CATEGORIES.find((c) => c.id === categoryId);
  const navigate = useNavigate();
  const cart = useCart();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Redirect if not found
  useEffect(() => {
    if (!category) navigate('/shop', { replace: true });
  }, [category, navigate]);

  // Mobile detection
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Enable scrolling
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    html.style.overflow = 'auto';
    html.style.height = 'auto';
    body.style.overflow = 'auto';
    body.style.height = 'auto';
    body.style.position = 'static';
    if (root) {
      root.style.overflow = 'auto';
      root.style.height = 'auto';
      root.style.position = 'static';
    }
    return () => {
      html.style.overflow = 'hidden';
      html.style.height = '100%';
      body.style.overflow = 'hidden';
      body.style.height = '100%';
      body.style.position = 'fixed';
      if (root) {
        root.style.overflow = 'hidden';
        root.style.height = '100%';
        root.style.position = 'fixed';
      }
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categoryId]);

  if (!category) return null;

  const products = PRODUCTS.filter((p) => p.categoryId === categoryId);

  return (
    <div style={{ background: '#000', minHeight: '100vh' }}>
      {/* Navigation */}
      <nav
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          padding: isMobile ? '16px 12px' : '32px 48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(rgba(0,0,0,0.8), transparent)',
        }}
      >
        <div style={{ width: 80 }}>
          <button
            onClick={() => navigate('/shop')}
            style={{
              background: 'none',
              border: 'none',
              color: '#ffffff',
              cursor: 'pointer',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 300,
              fontSize: 13,
              letterSpacing: '0.1em',
              padding: 0,
            }}
          >
            &larr; SHOP
          </button>
        </div>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <h1
            onClick={() => navigate('/')}
            className="tracking-widest uppercase cursor-pointer"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 300,
              fontSize: isMobile ? '14px' : '22px',
              color: '#ffffff',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              margin: 0,
            }}
          >
            Joshua Gavin
          </h1>
        </div>
        <div style={{ width: 80, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 20 }}>
          <button
            onClick={() => setIsCartOpen(true)}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, position: 'relative' }}
            aria-label="Open cart"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 01-8 0" />
            </svg>
            {cart.cartCount > 0 && (
              <span style={{ position: 'absolute', top: -6, right: -8, background: '#36454F', color: '#fff', fontSize: 10, fontWeight: 600, width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Montserrat, sans-serif' }}>
                {cart.cartCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setIsMenuOpen(true)}
            className="flex flex-col items-end gap-2 bg-transparent border-none cursor-pointer p-0 group"
            aria-label="Open menu"
          >
            <span className="w-8 h-0.5 bg-white block transition-all duration-300 group-hover:w-12" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }} />
            <span className="w-8 h-0.5 bg-white block transition-all duration-300 group-hover:w-12" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }} />
            {isMobile && (
              <span className="w-8 h-0.5 bg-white block transition-all duration-300 group-hover:w-12" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }} />
            )}
          </button>
        </div>
      </nav>

      {/* Category Header */}
      <div
        style={{
          paddingTop: isMobile ? 100 : 140,
          textAlign: 'center',
          paddingBottom: 20,
        }}
      >
        <h1
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 300,
            fontSize: 'clamp(28px, 5vw, 48px)',
            color: '#ffffff',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            margin: 0,
          }}
        >
          {category.title}
        </h1>
        <p
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 300,
            fontSize: 'clamp(12px, 2vw, 16px)',
            color: 'rgba(255,255,255,0.5)',
            letterSpacing: '0.08em',
            marginTop: 12,
          }}
        >
          {category.description}
        </p>
      </div>

      {/* Products */}
      {products.length > 0 ? (
        <ProductGrid products={products} />
      ) : (
        <p
          style={{
            textAlign: 'center',
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 300,
            fontSize: 15,
            color: 'rgba(255,255,255,0.4)',
            padding: '60px 24px',
          }}
        >
          Coming soon
        </p>
      )}

      {/* Footer */}
      <footer
        style={{
          textAlign: 'center',
          padding: '48px 24px 64px',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          marginTop: 40,
        }}
      >
        <p
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 300,
            fontSize: 12,
            color: 'rgba(255,255,255,0.25)',
            letterSpacing: '0.05em',
          }}
        >
          &copy; {new Date().getFullYear()} Joshua Gavin. All rights reserved.
        </p>
      </footer>

      <MenuOverlay isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} cart={cart} />
    </div>
  );
};

export default Collection;
